import FormModal from '../../../Modals/FormModal';

import InputField from '../../../Forms/Input/InputField';

import { useQuotation } from '../../../../hooks/sale/useQuotation';

interface Props {
  isOpen: boolean;

  onClose: () => void;

  onSave: (updated: {
    itemId: number;

    productId: number;

    quantity: number;

    unitPrice: number;

    discountPercent: number;
  }) => void;
}

const QuotationUpdateSelectedProductModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
}) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================

  const {
    form,

    selectedProduct,

    handlerUpdateSelectedProductField,
  } = useQuotation();

  // =====================================================
  // 🔹 Guards
  // =====================================================

  if (!selectedProduct) {
    return null;
  }

  const productExcluded = selectedProduct.appliesDiscount === false;

  const globalDiscountActive = Number(form?.globalDiscountPercent || 0) > 0;

  const discountDisabled = productExcluded;

  // =====================================================
  // 🔹 Handlers
  // =====================================================

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      handlerUpdateSelectedProductField({
        quantity: undefined,
      });

      return;
    }

    const parsed = Number(value);

    if (!isNaN(parsed) && parsed >= 0) {
      handlerUpdateSelectedProductField({
        quantity: parsed,
      });
    }
  };

  const handleUnitPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      handlerUpdateSelectedProductField({
        unitPrice: undefined,
      });

      return;
    }

    const parsed = Number(value);

    if (!isNaN(parsed) && parsed >= 0) {
      handlerUpdateSelectedProductField({
        unitPrice: parsed,
      });
    }
  };

  const handleDiscountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '') {
      handlerUpdateSelectedProductField({
        discountPercent: undefined,
      });

      return;
    }

    const parsed = Number(value);

    if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
      handlerUpdateSelectedProductField({
        discountPercent: parsed,
      });
    }
  };

  const handleSubmit = () => {
    onSave({
      itemId: selectedProduct.id,

      productId: selectedProduct.productId,

      quantity: Number(selectedProduct.quantity),

      unitPrice: Number(selectedProduct.unitPrice),

      discountPercent: Number(selectedProduct.discountPercent || 0),
    });

    onClose();
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar producto"
      maxWidth="max-w-md"
    >
      <div className="flex flex-col gap-5">
        {/* ===================================================== */}
        {/* 🔹 Product */}
        {/* ===================================================== */}

        <div>
          <h3
            className="
              text-sm
              font-semibold
              text-black

              dark:text-white
            "
          >
            {selectedProduct.productName}
          </h3>

          <p
            className="
              mt-1
              text-xs
              text-body
            "
          >
            SKU: {selectedProduct.sku || 'N/A'}
          </p>
        </div>

        {/* ===================================================== */}
        {/* 🔹 Inputs */}
        {/* ===================================================== */}

        <div className="grid grid-cols-2 gap-4">
          <InputField
            label="Cantidad"
            name="quantity"
            type="number"
            min={0}
            iconKey="hash"
            value={
              selectedProduct.quantity === 0 ? '' : selectedProduct.quantity
            }
            onChange={handleQuantityChange}
          />

          <InputField
            label="Precio"
            name="unitPrice"
            type="number"
            step="0.01"
            min={0}
            iconKey="dollar"
            value={
              selectedProduct.unitPrice === 0 ? '' : selectedProduct.unitPrice
            }
            onChange={handleUnitPriceChange}
          />
        </div>

        {/* ===================================================== */}
        {/* 🔹 Discount */}
        {/* ===================================================== */}

        <InputField
          label="Descuento (%)"
          name="discountPercent"
          type="number"
          step="0.01"
          min={0}
          max={100}
          iconKey="percentage"
          disabled={discountDisabled}
          value={
            selectedProduct.discountPercent === 0
              ? ''
              : selectedProduct.discountPercent
          }
          onChange={handleDiscountChange}
        />

        {productExcluded && (
          <p className="-mt-3 text-xs text-bodydark2">
            Este producto no admite descuentos.
          </p>
        )}

        {!productExcluded && globalDiscountActive && (
          <p className="-mt-3 text-xs text-bodydark2">
            Hay un descuento global activo. Si pones un descuento manual
            aquí, se te pedirá confirmar y el global se quitará.
          </p>
        )}

        {/* ===================================================== */}
        {/* 🔹 Actions */}
        {/* ===================================================== */}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="
              rounded-lg
              border
              border-stroke

              px-4
              py-2

              text-sm
              font-medium

              transition-all

              hover:bg-whiten

              dark:border-strokedark
              dark:hover:bg-meta-4
            "
          >
            Cancelar
          </button>

          <button
            onClick={handleSubmit}
            className="
              rounded-lg

              bg-primary

              px-4
              py-2

              text-sm
              font-medium
              text-white

              transition-all

              hover:bg-opacity-90
            "
          >
            Guardar
          </button>
        </div>
      </div>
    </FormModal>
  );
};

export default QuotationUpdateSelectedProductModal;
